import { Check } from "lucide-react";
import { isPublicEvalType } from "@/utils/constants";
import { getSizeNumeric } from "@/utils/sizeUtils";
import { parsePrice } from "@/utils/priceUtils";

const CONSISTENCY_MAP = {
  "blusky": "30%", "blusky-trading": "30%",
  "elite-trader-funding": "35%", "top-one-futures": "20%",
  "blue-guardian": "30%", "aquafutures": "25%", "yrm-prop": "30%",
};

// Known popular/badge hints by numeric size
const SIZE_HINTS = {
  50000: { badge: "BEST VALUE" },
  100000: { badge: "MOST POPULAR" },
};

export const AccountSizeCards = ({ selected, onSelect, firms }) => {

  // Derive available sizes dynamically from firm pricing data (standard eval types only)
  const availableSizes = (() => {
    const sizeSet = new Map();
    for (const f of firms) {
      for (const p of f.pricing || []) {
        if (!isPublicEvalType(p.evaluation_type)) continue;
        const priceNum = parsePrice(p.price);
        if (!priceNum || priceNum <= 0) continue;
        const numericKey = getSizeNumeric(p);
        if (!numericKey) continue;
        if (!sizeSet.has(numericKey)) sizeSet.set(numericKey, { numericSize: numericKey, displaySize: p.size, count: 0 });
        sizeSet.get(numericKey).count++;
      }
    }
    return [...sizeSet.values()]
      .sort((a, b) => a.numericSize - b.numericSize)
      .map(s => ({
        numericSize: s.numericSize,
        size: s.numericSize,
        label: s.displaySize?.replace("$", "") || `${s.numericSize / 1000}K`,
        badge: SIZE_HINTS[s.numericSize]?.badge || null,
        firmCount: s.count,
      }));
  })();

  const getLowestPrice = (size) => {
    const prices = firms.map(f => {
      const p = f.pricing?.find(pr => getSizeNumeric(pr) === size && isPublicEvalType(pr.evaluation_type));
      if (!p) return Infinity;
      return parsePrice(p.price);
    }).filter(n => n !== Infinity && n > 0);
    return prices.length ? `$${Math.min(...prices)}` : "N/A";
  };

  const getBuffer = (size) => {
    const buffers = firms.map(f => {
      const p = f.pricing?.find(pr => getSizeNumeric(pr) === size && isPublicEvalType(pr.evaluation_type));
      return p?.buffer || null;
    }).filter(Boolean);
    return buffers[0] || "N/A";
  };

  const handleSelect = (size) => {
    // Toggle: tap again to deselect
    onSelect(selected === size ? null : size);
  };

  return (
    <div className="py-10 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-1">
              Select Account <span className="text-[#2979FF]">Size</span>
            </h2>
            <p className="text-sm text-slate-500">Pick a size to compare real pricing</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="account-size-cards">
          {availableSizes.map((item) => {
            const isSelected = selected === item.size;
            return (
              <button
                key={item.size}
                onClick={() => handleSelect(item.size)}
                data-testid={`size-card-${item.label.toLowerCase()}`}
                className={`group relative rounded-2xl transition-all duration-300 overflow-hidden ${
                  isSelected
                    ? "ring-2 ring-[#2979FF] shadow-2xl shadow-blue-500/15 scale-[1.03]"
                    : "hover:scale-[1.02] hover:shadow-lg"
                }`}
              >
                {item.badge && (
                  <span className={`absolute -top-0 left-0 right-0 text-center py-1 text-[9px] font-bold tracking-wider z-10 ${
                    item.badge === "MOST POPULAR" ? "bg-orange-500 text-white" : "bg-emerald-500 text-white"
                  }`}>
                    {item.badge}
                  </span>
                )}

                <div className={`relative p-5 ${item.badge ? 'pt-7' : 'pt-5'} ${
                  isSelected
                    ? "bg-gradient-to-b from-[#2979FF]/20 to-slate-900 border border-[#2979FF]/40"
                    : "bg-slate-800/40 border border-slate-700/40 group-hover:border-slate-600/60 group-hover:bg-slate-800/60"
                }`}>
                  <p className={`text-3xl font-black tracking-tight mb-1 ${isSelected ? 'text-white' : 'text-slate-200 group-hover:text-white'}`}>
                    {item.label}
                  </p>
                  <div className={`h-px my-3 ${isSelected ? 'bg-[#2979FF]/40' : 'bg-slate-700/60'}`} />
                  <div className="text-left">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium mb-0.5">From</p>
                    <p className={`text-lg font-bold font-mono ${isSelected ? 'text-[#2979FF]' : 'text-slate-300'}`}>
                      {getLowestPrice(item.size)}
                    </p>
                  </div>
                  <div className="text-left mt-2">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium mb-0.5">Buffer</p>
                      <p className={`text-xs font-mono font-semibold ${isSelected ? 'text-emerald-400' : 'text-slate-400'}`}>
                        {getBuffer(item.size)}
                      </p>
                    </div>
                  {isSelected && (
                    <div className="absolute top-3 right-3 w-5 h-5 bg-[#2979FF] rounded-full flex items-center justify-center">
                      <Check size={12} className="text-white" />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
